// @ts-check
// 8ft trees (2026-10-03) — the cart page keeps the 8ft rules true after every
// change (it re-runs on each `cartUpdate`, because the cart re-renders its
// footer over AJAX):
//   * a DELIVERED 8ft tree carries exactly ONE $15 "8ft Delivery Surcharge"
//     line — added if missing (a customer removing it can't check out at $0),
//     clamped to 1 (two 8ft trees still pay one flat fee);
//   * no 8ft tree, or Click & Collect → the surcharge is removed;
//   * an 8ft delivery dated off the region's 8ft days → explained, checkout held.
// Fails OPEN on network errors — the staff app's Scheduling checks catch what
// slips through.

const API = 'https://api.thetreeelves.com.au/post_codes/'

/** Same precedence as post-code-picker.js isEightFtTree. */
const isEightFtTree = (/** @type {unknown} */ productTitle, /** @type {unknown} */ variantTitle) => {
  if (!String(productTitle ?? '').toLowerCase().includes('real christmas tree')) return false
  const v = String(variantTitle ?? '').toLowerCase()
  if (/extra[\s-]*large|\bxl\b/.test(v)) return true
  if (/large|medium|small/.test(v)) return false
  const m =
    /(\d+(?:\.\d+)?)\s*[-–]\s*\d+(?:\.\d+)?\s*(?:ft|foot|feet|')/.exec(v) ??
    /(\d+(?:\.\d+)?)\s*(?:ft|foot|feet|')/.exec(v)
  return m ? Number.parseFloat(m[1]) > 7.5 : false
}
const isSurcharge = (/** @type {any} */ i) => String(i?.product_title ?? '').toLowerCase().includes('8ft delivery surcharge')

const post = (/** @type {string} */ url, /** @type {object} */ body) =>
  fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })

let running = false
const check = async () => {
  if (running) return
  running = true
  try {
    const cart = await (await fetch('/cart.js')).json()
    const attrs = cart?.attributes ?? {}
    const items = cart?.items ?? []
    const delivery = attrs['Shipping method'] === 'Delivery'
    const hasEightFt = items.some((/** @type {any} */ i) => isEightFtTree(i.product_title, i.variant_title))
    const fees = items.filter(isSurcharge)
    const surchargeId = Number(/** @type {any} */ (window).TTE_8FT_SURCHARGE) || null

    // 1. Keep the surcharge to exactly what the cart needs.
    // Reload only after a change Shopify ACCEPTED — a refused add must not
    // become a reload loop.
    let changed = false
    if (!(delivery && hasEightFt)) {
      for (const f of fees) changed = (await post('/cart/change.js', { id: f.key, quantity: 0 })).ok || changed
    } else if (fees.length === 0 && surchargeId) {
      changed = (await post('/cart/add.js', { items: [{ id: surchargeId, quantity: 1 }] })).ok
    } else if (fees.length > 1 || (fees[0] && fees[0].quantity !== 1)) {
      changed = (await post('/cart/change.js', { id: fees[0].key, quantity: 1 })).ok
      for (const f of fees.slice(1)) changed = (await post('/cart/change.js', { id: f.key, quantity: 0 })).ok || changed
    }
    if (changed) { window.location.reload(); return }

    // 2. An 8ft delivery must be on one of the region's 8ft days.
    if (!delivery || !hasEightFt) return
    const postCode = String(attrs['Post Code'] ?? '').trim()
    const date = String(attrs['Shipping Date'] ?? '').trim()
    if (!/^\d{3,4}$/.test(postCode) || !date) return
    const preview = /** @type {any} */ (window).TTE_8FT_PREVIEW === true ? '?preview_8ft=1' : ''
    const res = await fetch(API + postCode + preview)
    if (!res.ok) return
    const data = await res.json()
    const eightFtDates = data?.Eight_Ft?.Delivery_dates
    if (Array.isArray(eightFtDates) && eightFtDates.includes(date)) return
    const box = document.getElementById('cart-errors')
    if (box) {
      box.textContent =
        `Our 8ft trees are delivered by two elves on set days, and ${date} isn't one of them for your area. ` +
        'Open the tree again to pick an 8ft delivery day, or choose Click & Collect.'
      box.setAttribute('role', 'alert')
    }
    document.querySelectorAll('[data-component-checkout-button]').forEach((b) => b.setAttribute('disabled', ''))
  } catch (error) {
    console.error('8ft cart check skipped', error)
  } finally {
    running = false
  }
}

check()
// The cart re-renders over AJAX; re-check after every change. pubsub.js and
// constants.js load after this script, so subscribe once the page is ready.
document.addEventListener('DOMContentLoaded', () => {
  const w = /** @type {any} */ (window)
  if (typeof w.subscribe === 'function' && w.PUB_SUB_EVENTS?.cartUpdate) w.subscribe(w.PUB_SUB_EVENTS.cartUpdate, () => check())
})
